import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Tabs,
	Tab,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonIcon from "@mui/icons-material/Person";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ImportarUsuariosDialog from "./ImportarUsuariosDialog";
import { UsuarioDistribuidorService } from "../../service/usuario_distribuidor_service";

function Usuarios() {
	const navigate = useNavigate();
	const [usuarios, setUsuarios] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [total, setTotal] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [itemToToggle, setItemToToggle] = useState(null);
	const [abaAtiva, setAbaAtiva] = useState(0);
	const [openImportar, setOpenImportar] = useState(false);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const loadUsuarios = useCallback(async () => {
		setLoading(true);
		try {
			const usuarioService = new UsuarioDistribuidorService();
			const ativo = abaAtiva === 0;
			const result = await usuarioService.getAll(
				unidadeDistribuidoraId,
				page + 1,
				rowsPerPage,
				searchTerm,
				ativo
			);
			setUsuarios(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Erro ao carregar usuários:", error);
			alert(error.message || "Erro ao carregar usuários");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, page, rowsPerPage, searchTerm, abaAtiva]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			loadUsuarios();
		}
	}, [unidadeDistribuidoraId, loadUsuarios]);

	// Debounced search effect
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setPage(0);
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [searchTerm]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenMenu = (event, item) => {
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleEdit = () => {
		if (selectedItem) {
			navigate(`/usuarios/cadastro/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleToggleStatusClick = () => {
		setItemToToggle(selectedItem);
		setOpenDialog(true);
		handleCloseMenu();
	};

	const handleConfirmToggleStatus = async () => {
		if (itemToToggle) {
			try {
				const usuarioService = new UsuarioDistribuidorService();
				await usuarioService.toggleStatus(itemToToggle.id);
				setOpenDialog(false);
				setItemToToggle(null);
				loadUsuarios();
			} catch (error) {
				console.error("Erro ao alterar status do usuário:", error);
				alert(error.message || "Erro ao alterar status do usuário");
			}
		}
	};

	const handleCancelToggle = () => {
		setOpenDialog(false);
		setItemToToggle(null);
	};

	const handleChangeTab = (event, newValue) => {
		setAbaAtiva(newValue);
		setPage(0);
	};

	return (
		<AppLayout currentPage="usuarios">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#666",
								fontWeight: 600,
							}}
						>
							Usuários
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="outlined"
								startIcon={<UploadFileIcon />}
								onClick={() => setOpenImportar(true)}
								sx={{
									borderColor: "#1a9e8f",
									color: "#1a9e8f",
									"&:hover": { borderColor: "#138a7d", color: "#138a7d", bgcolor: "rgba(26, 158, 143, 0.06)" },
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Importar planilha
							</Button>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={() => navigate("/usuarios/cadastro")}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
									},
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Novo Usuário
							</Button>
						</Box>
					</Box>

					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
							overflow: "hidden",
						}}
					>
						<Tabs
							value={abaAtiva}
							onChange={handleChangeTab}
							sx={{
								borderBottom: 1,
								borderColor: "divider",
								bgcolor: "#f5f5f5",
							}}
						>
							<Tab
								label="Ativos"
								sx={{
									textTransform: "none",
									fontSize: "15px",
									fontWeight: 500,
								}}
							/>
							<Tab
								label="Desativados"
								sx={{
									textTransform: "none",
									fontSize: "15px",
									fontWeight: 500,
								}}
							/>
						</Tabs>

						{loading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "400px",
								}}
							>
								<CircularProgress sx={{ color: "#1a9e8f" }} />
							</Box>
						) : usuarios.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "400px",
									gap: 2,
									p: 4,
								}}
							>
								<Typography
									variant="h6"
									sx={{ color: "#999", textAlign: "center" }}
								>
									Nenhum usuário encontrado
								</Typography>
								<Typography
									variant="body2"
									sx={{ color: "#999", textAlign: "center" }}
								>
									{searchTerm
										? "Tente fazer uma busca diferente"
										: 'Clique no botão "Novo Usuário" para começar'}
								</Typography>
							</Box>
						) : (
							<>
								<TableContainer>
									<Table>
										<TableHead>
											<TableRow sx={{ bgcolor: "#f5f5f5" }}>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Nome
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Email
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Telefone
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Unidades
												</TableCell>
												<TableCell
													sx={{ fontWeight: 600, color: "#666", width: "80px" }}
													align="center"
												>
													Ações
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{usuarios.map((usuario) => (
												<TableRow
													key={usuario.id}
													sx={{
														"&:hover": {
															bgcolor: "#f9f9f9",
														},
													}}
												>
													<TableCell>
														<Typography variant="body2" sx={{ fontWeight: 500 }}>
															{usuario.nome}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{usuario.email || "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{usuario.telefone || "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{usuario.unidadesDistribuidoras?.length || 0}{" "}
															{usuario.unidadesDistribuidoras?.length === 1
																? "unidade"
																: "unidades"}
														</Typography>
													</TableCell>
													<TableCell align="center">
														<IconButton
															size="small"
															onClick={(e) => handleOpenMenu(e, usuario)}
															sx={{ color: "#666" }}
														>
															<MoreVertIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									component="div"
									count={total}
									page={page}
									onPageChange={handleChangePage}
									rowsPerPage={rowsPerPage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									rowsPerPageOptions={[5, 10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) =>
										`${from}-${to} de ${count}`
									}
									sx={{
										borderTop: "1px solid #e0e0e0",
										".MuiTablePagination-select": {
											borderRadius: "4px",
										},
									}}
								/>
							</>
						)}
					</Paper>
			</Box>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleEdit}>
					<EditIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Editar
				</MenuItem>
				<MenuItem onClick={handleToggleStatusClick}>
					{abaAtiva === 0 ? (
						<>
							<PersonOffIcon fontSize="small" sx={{ mr: 1, color: "#ff4d4d" }} />
							Desativar
						</>
					) : (
						<>
							<PersonIcon fontSize="small" sx={{ mr: 1, color: "#28A745" }} />
							Ativar
						</>
					)}
				</MenuItem>
			</Menu>

			<Dialog open={openDialog} onClose={handleCancelToggle}>
				<DialogTitle>
					{abaAtiva === 0 ? "Confirmar Desativação" : "Confirmar Ativação"}
				</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja {abaAtiva === 0 ? "desativar" : "ativar"} o
						usuário "{itemToToggle?.nome}"?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelToggle} sx={{ color: "#666" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmToggleStatus}
						variant="contained"
						sx={{
							bgcolor: abaAtiva === 0 ? "#ff4d4d" : "#28A745",
							"&:hover": {
								bgcolor: abaAtiva === 0 ? "#e64545" : "#218838",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>

			<ImportarUsuariosDialog
				open={openImportar}
				onClose={() => setOpenImportar(false)}
				onImportado={() => {
					setPage(0);
					loadUsuarios();
				}}
				unidadeDistribuidoraId={unidadeDistribuidoraId}
			/>
		</AppLayout>
	);
}

export default Usuarios;
